// src/ForecastApp.js
import React, { useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import { FaChartLine, FaSearch } from "react-icons/fa";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { googleApi } from "../../services/api";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ForecastApp = () => {
  const [packageName, setPackageName] = useState("");
  const [forecastData, setForecastData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const labelDate = (row) => {
    const raw = row?.ds ?? row?.date ?? "";
    return String(raw).split("T")[0];
  };

  const n = (v) => (typeof v === "number" ? v : parseFloat(v ?? 0) || 0);

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await googleApi.get("forecast/", {
        params: { package_name: packageName },
      });
      setForecastData(Array.isArray(data?.forecast) ? data.forecast : []);
    } catch (err) {
      console.error(err);
      setError("Error fetching data. Please check the package name.");
      setForecastData([]);
    } finally {
      setLoading(false);
    }
  };

  // Chart datasets
  const labels = forecastData.map(labelDate);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Predicted Installs",
        data: forecastData.map((r) => n(r["Predicted Installs"])),
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        fill: true,
      },
      {
        label: "Lower Bound",
        data: forecastData.map((r) => n(r["Lower Bound"])),
        borderColor: "rgba(255, 99, 132, 1)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        fill: true,
      },
      {
        label: "Upper Bound",
        data: forecastData.map((r) => n(r["Upper Bound"])),
        borderColor: "rgba(255, 206, 86, 1)",
        backgroundColor: "rgba(255, 206, 86, 0.2)",
        fill: true,
      },
    ],
  };

  const waveLineChartData = {
    labels,
    datasets: [
      {
        label: "Predicted Installs",
        data: forecastData.map((r) => n(r["Predicted Installs"])),
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        fill: false,
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "rgba(54, 162, 235, 1)",
      },
      {
        label: "Lower Bound",
        data: forecastData.map((r) => n(r["Lower Bound"])),
        borderColor: "rgba(255, 99, 132, 1)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        fill: false,
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "rgba(255, 99, 132, 1)",
      },
      {
        label: "Upper Bound",
        data: forecastData.map((r) => n(r["Upper Bound"])),
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        fill: false,
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "rgba(75, 192, 192, 1)",
      },
    ],
  };

  const barChartData = {
    labels,
    datasets: [
      {
        label: "Predicted Installs",
        data: forecastData.map((r) => n(r["Predicted Installs"])),
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.4)",
        borderWidth: 2,
        fill: true,
      },
      {
        label: "Lower Bound",
        data: forecastData.map((r) => n(r["Lower Bound"])),
        borderColor: "rgba(255, 99, 71, 1)",
        backgroundColor: "rgba(255, 99, 71, 0.3)",
        borderWidth: 2,
        fill: true,
      },
      {
        label: "Upper Bound",
        data: forecastData.map((r) => n(r["Upper Bound"])),
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.3)",
        borderWidth: 2,
        fill: true,
      },
    ],
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!forecastData.length) return {};
    const installs = forecastData.map((r) => n(r["Predicted Installs"]));
    const total = installs.reduce((acc, v) => acc + v, 0);
    return {
      average: (total / installs.length).toFixed(2),
      max: Math.max(...installs),
      min: Math.min(...installs),
    };
  }, [forecastData]);

  return (
    <div className="container mt-4">
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaChartLine className="mr-3 text-blue-500" />
          App Forecasting
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-10">
                 <label className="block text-sm font-medium text-gray-700 mb-1">Enter Package Name</label>
                 <input
                    type="text"
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="e.g. com.example.app"
                    value={packageName}
                    onChange={(e) => setPackageName(e.target.value)}
                  />
            </div>
            <div className="md:col-span-2">
                 <button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    {loading ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    ) : (
                        <FaSearch className="mr-2" />
                    )}
                    {loading ? "Loading..." : "Forecast"}
                  </button>
            </div>
        </div>
      </div>

        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "300px" }}
          >
            <Spinner animation="border" role="status" />
            <span className="ms-2">Loading Please Wait...</span>
          </div>
        )}
        {error && <div className="alert alert-danger mt-2">{error}</div>}

      <div className="row">
        <div className="col-md-6">
          <div className="card p-3" style={{ backgroundColor: "#fff", borderRadius: "8px", height: "100%" }}>
            <h4 className="text-center" style={{ fontSize: "0.8rem" }}>
              Predicted Installs Over Time
            </h4>
            <Line data={chartData} />
          </div>
        </div>

        <div className="col-md-6">
          <div className="card p-3" style={{ backgroundColor: "#fff", borderRadius: "8px", height: "100%" }}>
            <h4 className="text-center" style={{ fontSize: "0.8rem" }}>
              <strong>Forecast Summary</strong>
            </h4>
            <div className="card-body" style={{ fontSize: "0.8rem" }}>
              <p><strong>Average Predicted Installs:</strong> {summaryStats.average}</p>
              <p><strong>Maximum Predicted Installs:</strong> {summaryStats.max}</p>
              <p><strong>Minimum Predicted Installs:</strong> {summaryStats.min}</p>
            </div>
            <div className="card-body mt-3" style={{ fontSize: "0.8rem", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid black" }}>
              <h5 className="text-center mb-2" style={{ color: "black" }}>
                <strong>User Insight</strong>
              </h5>
              <p>
                This forecast summary gives a clear view of your app's install projections, including average expectations and the range (lower/upper bounds). Use the range to plan for variability in campaigns and capacity.
              </p>
            </div>
          </div>
        </div>
      </div>

      {forecastData.length > 0 && (
        <table className="table table-bordered mt-4" style={{ fontSize: "0.6rem" }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Predicted Installs</th>
              <th>Lower Bound</th>
              <th>Upper Bound</th>
            </tr>
          </thead>
          <tbody>
            {forecastData.map((r, i) => (
              <tr key={i}>
                <td>{labelDate(r)}</td>
                <td>{n(r["Predicted Installs"])}</td>
                <td>{n(r["Lower Bound"])}</td>
                <td>{n(r["Upper Bound"])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row mt-4 d-flex align-items-stretch">
        <div className="col-md-6">
          <div className="card p-3" style={{ backgroundColor: "#fff", borderRadius: "8px", height: "100%" }}>
            <h4 className="text-center" style={{ fontSize: "1rem" }}>
              Predicted Installs, Lower & Upper Bounds (Wave)
            </h4>
            <Line data={waveLineChartData} options={{ responsive: true }} />
          </div>
        </div>

        <div className="col-md-6">
          <div className="card p-3" style={{ backgroundColor: "#fff", borderRadius: "8px", height: "100%" }}>
            <h4 className="text-center" style={{ fontSize: "0.8rem" }}>
              Bar Chart Comparison
            </h4>
            <Bar data={barChartData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastApp;
